import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { dashboardDetailsAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { formatDateTime } from '../lib/timeFormat';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

export const TodayLeadsPage = () => {
  const { user } = useAuth();
  const tf = user?.time_format || '12h';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ created_by: '', assigned_to: '', source: '', service: '', city: '', status: '', priority: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dashboardDetailsAPI.newLeadsToday();
      const data = res.data;
      setRows(Array.isArray(data) ? data : (data.leads || data.items || []));
    } catch (e) { toast.error('Failed to load today\u2019s leads'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const opts = useMemo(() => {
    const uniq = (fn) => Array.from(new Set(rows.map(fn).filter(Boolean))).sort();
    return {
      created_by: uniq(r => r.created_by_name || r.created_by),
      assigned_to: uniq(r => r.assigned_to_name || r.assigned_to),
      source: uniq(r => r.lead_source),
      service: uniq(r => (r.services_required && r.services_required[0]) || r.service),
      city: uniq(r => r.city),
      status: uniq(r => r.status),
      priority: uniq(r => r.priority),
    };
  }, [rows]);

  const filtered = rows.filter(r => {
    const m = (v, f) => !f || String(v || '').toLowerCase() === String(f).toLowerCase();
    return m(r.created_by_name || r.created_by, filters.created_by)
      && m(r.assigned_to_name || r.assigned_to, filters.assigned_to)
      && m(r.lead_source, filters.source)
      && m((r.services_required && r.services_required[0]) || r.service, filters.service)
      && m(r.city, filters.city) && m(r.status, filters.status) && m(r.priority, filters.priority);
  });

  const F = ({ name, label }) => (
    <div>
      <label className="block text-xs font-medium mb-1">{label}</label>
      <select value={filters[name]} onChange={e => setFilters({ ...filters, [name]: e.target.value })} data-testid={`tl-filter-${name}`}
        className="w-full px-2 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm">
        <option value="">All</option>
        {(opts[name] || []).map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );

  if (loading) return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;

  return (
    <div className="space-y-6" data-testid="today-leads-page">
      <div>
        <h1 className="text-3xl font-outfit font-semibold">Today's Leads</h1>
        <p className="text-muted-foreground mt-1">{filtered.length} of {rows.length} leads created today</p>
      </div>
      <div className="clay-card p-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <F name="created_by" label="Created By" />
        <F name="assigned_to" label="Assigned To" />
        <F name="source" label="Source" />
        <F name="service" label="Service" />
        <F name="city" label="City" />
        <F name="status" label="Status" />
        <F name="priority" label="Priority" />
      </div>
      <div className="clay-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800/50 text-left">
            <tr>
              {['Client', 'Created By', 'Assigned To', 'Source', 'Service', 'City', 'Budget', 'Status', 'Priority', 'Created'].map(h => <th key={h} className="px-3 py-3 font-medium">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? <tr><td colSpan={10} className="px-4 py-8 text-center text-muted-foreground">No leads match.</td></tr> :
              filtered.map(r => (
                <tr key={r.id} className="border-t border-slate-100 dark:border-slate-800" data-testid="tl-row">
                  <td className="px-3 py-2"><Link to={`/leads/${r.id}`} className="text-primary hover:underline">{r.customer_name}</Link></td>
                  <td className="px-3 py-2">{r.created_by_name || '\u2014'}</td>
                  <td className="px-3 py-2">{r.assigned_to_name || '\u2014'}</td>
                  <td className="px-3 py-2">{r.lead_source || '\u2014'}</td>
                  <td className="px-3 py-2">{(r.services_required && r.services_required[0]) || r.service || '\u2014'}</td>
                  <td className="px-3 py-2">{r.city || '\u2014'}</td>
                  <td className="px-3 py-2">{r.budget != null ? `\u20b9${Number(r.budget).toLocaleString('en-IN')}` : '\u2014'}</td>
                  <td className="px-3 py-2">{r.status || '\u2014'}</td>
                  <td className="px-3 py-2 capitalize">{r.priority || '\u2014'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.created_at ? formatDateTime(r.created_at, tf) : '\u2014'}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
export default TodayLeadsPage;
